'use client';

import { useEffect, useRef } from 'react';
import { useSeqLog } from '@/hooks/telemetry';
import {
  LANDING_PERF_HERO_COMPONENT,
  LANDING_PERF_HERO_SEQUENCE_MESSAGE,
  LANDING_PERF_HERO_TEAMS_DATA_MESSAGE,
} from './Chero.constants';

interface UseHeroTelemetryInput {
  viewportLoaded: boolean;
  hasTeams: boolean;
  isLogoReady: boolean;
  isWaveReady: boolean;
  isNavReady: boolean;
  isTeamsMounted: boolean;
  isTeamsSettled: boolean;
  isTeamsVisible: boolean;
  teamsLength: number;
  heroTeamsDataLength: number;
  teamsQueryStatus: string;
}

function getElapsedHeroMountMs(heroMountMs: number | null): number | null {
  if (heroMountMs == null || typeof performance === 'undefined') {
    return null;
  }

  return performance.now() - heroMountMs;
}

export function useHeroTelemetry({
  viewportLoaded,
  hasTeams,
  isLogoReady,
  isWaveReady,
  isNavReady,
  isTeamsMounted,
  isTeamsSettled,
  isTeamsVisible,
  teamsLength,
  heroTeamsDataLength,
  teamsQueryStatus,
}: UseHeroTelemetryInput): void {
  const landingPerfHeroLog = useSeqLog(LANDING_PERF_HERO_COMPONENT);
  const heroMountMsRef = useRef<number | null>(null);

  useEffect(() => {
    if (heroMountMsRef.current != null || typeof performance === 'undefined') {
      return;
    }

    heroMountMsRef.current = performance.now();
  }, []);

  useEffect(() => {
    landingPerfHeroLog(LANDING_PERF_HERO_SEQUENCE_MESSAGE, {
      viewportLoaded,
      hasTeams,
      isLogoReady,
      isWaveReady,
      isNavReady,
      isTeamsMounted,
      isTeamsSettled,
      isTeamsVisible,
      elapsedFromHeroMountMs: getElapsedHeroMountMs(heroMountMsRef.current),
    });
  }, [
    hasTeams,
    isLogoReady,
    isNavReady,
    isTeamsMounted,
    isTeamsSettled,
    isTeamsVisible,
    isWaveReady,
    landingPerfHeroLog,
    viewportLoaded,
  ]);

  useEffect(() => {
    landingPerfHeroLog(LANDING_PERF_HERO_TEAMS_DATA_MESSAGE, {
      teamsLength,
      heroTeamsDataLength,
      hasTeams,
      teamsQueryStatus,
      elapsedFromHeroMountMs: getElapsedHeroMountMs(heroMountMsRef.current),
    });
  }, [
    hasTeams,
    heroTeamsDataLength,
    landingPerfHeroLog,
    teamsLength,
    teamsQueryStatus,
  ]);
}
